import type { CustomPageFormat } from '@tiptap-pro/extension-pages';
import type { NumberingFormatDefinition } from '@tiptap-pro/extension-export-docx';
import type { Doc as YDoc, Map as YMap } from 'yjs';

export enum DocumentViewMode {
  Pageless = 'pageless',
  Pages = 'pages',
}
export type DocumentPageSize = 'A4' | 'A3' | 'A5' | 'USLetter' | 'USLegal' | 'Tabloid';
export type DocumentOrientation = 'portrait' | 'landscape';
export type DocumentMargins = 'narrow' | 'normal' | 'wide';

export interface DocumentHeaderFooterVariants {
  first?: string;
}

export interface DocumentHeaderSettings {
  show: boolean;
  locked: boolean;
  topMarginPx: number;
  content: string;
  differentFirstPage: boolean;
  variants?: DocumentHeaderFooterVariants;
}

export interface DocumentFooterSettings {
  show: boolean;
  locked: boolean;
  bottomMarginPx: number;
  content: string;
  differentFirstPage: boolean;
  variants?: DocumentHeaderFooterVariants;
}

export interface DocumentSettings {
  viewMode: DocumentViewMode;
  pageSize: DocumentPageSize;
  orientation: DocumentOrientation;
  margins: DocumentMargins;
  header: DocumentHeaderSettings | null;
  footer: DocumentFooterSettings | null;
}

const PAGE_SIZE_MM: Record<DocumentPageSize, { widthMm: number; heightMm: number }> = {
  A4: { widthMm: 210, heightMm: 297 },
  A3: { widthMm: 297, heightMm: 420 },
  A5: { widthMm: 148, heightMm: 210 },
  USLetter: { widthMm: 215.9, heightMm: 279.4 },
  USLegal: { widthMm: 215.9, heightMm: 355.6 },
  Tabloid: { widthMm: 279.4, heightMm: 431.8 },
};

const MARGIN_MM: Record<DocumentMargins, number> = {
  narrow: 12.7,
  normal: 25.4,
  wide: 38.1,
};

const mmToPx = (mm: number): number => Math.round((mm / 25.4) * 96);

export function toPagesCustomFormat({
  pageSize,
  orientation,
  margins,
}: {
  pageSize: DocumentPageSize;
  orientation: DocumentOrientation;
  margins: DocumentMargins;
}): CustomPageFormat {
  const { widthMm, heightMm } = PAGE_SIZE_MM[pageSize];
  const isLandscape = orientation === 'landscape';
  const marginPx = mmToPx(MARGIN_MM[margins]);

  return {
    id: `${pageSize}-${orientation}-${margins}`,
    width: mmToPx(isLandscape ? heightMm : widthMm),
    height: mmToPx(isLandscape ? widthMm : heightMm),
    margins: {
      top: marginPx,
      right: marginPx,
      bottom: marginPx,
      left: marginPx,
    },
  };
}

// Used when an inline header/footer edit needs to materialise a settings object
// that doesn't exist yet. Margins mirror the Settings tab defaults (32px).
export const DEFAULT_HEADER_SETTINGS: DocumentHeaderSettings = {
  show: true,
  locked: false,
  topMarginPx: 32,
  content: '',
  differentFirstPage: false,
  variants: {},
};

export const DEFAULT_FOOTER_SETTINGS: DocumentFooterSettings = {
  show: true,
  locked: false,
  bottomMarginPx: 32,
  content: '',
  differentFirstPage: false,
  variants: {},
};

export const DEFAULT_DOCUMENT_SETTINGS: DocumentSettings = {
  viewMode: DocumentViewMode.Pageless,
  pageSize: 'A4',
  orientation: 'portrait',
  margins: 'normal',
  header: null,
  footer: null,
};

export const SETTINGS_MAP_KEY = 'documentSettings';
const NUMBERING_FORMATS_MAP_KEY = 'numberingFormatsById';
const LEGACY_NUMBERING_FORMATS_KEY = 'numberingFormats';

/**
 * Marks Y.Doc transactions that originate from this client's own edits.
 * The collaborator-conflict observer ignores changes carrying this origin
 * so the local user is never told their own save "came from another user".
 */
export const LOCAL_SETTINGS_ORIGIN = Symbol('document-settings:local');

export const HEADER_FOOTER_UNDO_ORIGIN = Symbol('document-settings:header-footer');

const isLocalOrigin = (origin: unknown): boolean =>
  origin === LOCAL_SETTINGS_ORIGIN || origin === HEADER_FOOTER_UNDO_ORIGIN;

export function getDocumentSettingsMap(ydoc: YDoc): YMap<unknown> {
  return ydoc.getMap(SETTINGS_MAP_KEY);
}

export function readDocumentSettings(ydoc: YDoc): DocumentSettings {
  const map = getDocumentSettingsMap(ydoc);
  return {
    viewMode: (map.get('viewMode') as DocumentSettings['viewMode']) ?? DEFAULT_DOCUMENT_SETTINGS.viewMode,
    pageSize: (map.get('pageSize') as DocumentSettings['pageSize']) ?? DEFAULT_DOCUMENT_SETTINGS.pageSize,
    orientation:
      (map.get('orientation') as DocumentSettings['orientation']) ?? DEFAULT_DOCUMENT_SETTINGS.orientation,
    margins: (map.get('margins') as DocumentSettings['margins']) ?? DEFAULT_DOCUMENT_SETTINGS.margins,
    header: (map.get('header') as DocumentSettings['header']) ?? DEFAULT_DOCUMENT_SETTINGS.header,
    footer: (map.get('footer') as DocumentSettings['footer']) ?? DEFAULT_DOCUMENT_SETTINGS.footer,
  };
}

export function writeDocumentSettings(
  ydoc: YDoc,
  settings: Partial<DocumentSettings>,
  origin: symbol = LOCAL_SETTINGS_ORIGIN
): void {
  const map = getDocumentSettingsMap(ydoc);
  ydoc.transact(() => {
    for (const [key, value] of Object.entries(settings)) {
      map.set(key, value);
    }
  }, origin);
}

type DocumentSettingsObserveHandler = (info: { isLocal: boolean }) => void;

export function getNumberingFormatsMap(ydoc: YDoc): YMap<NumberingFormatDefinition> {
  return ydoc.getMap(NUMBERING_FORMATS_MAP_KEY);
}

function readLegacyNumberingFormats(ydoc: YDoc): NumberingFormatDefinition[] {
  const legacy = getDocumentSettingsMap(ydoc).get(LEGACY_NUMBERING_FORMATS_KEY);
  return Array.isArray(legacy) ? (legacy as NumberingFormatDefinition[]) : [];
}

export function readNumberingFormats(ydoc: YDoc): NumberingFormatDefinition[] {
  const map = getNumberingFormatsMap(ydoc);
  if (map.size > 0) return Array.from(map.values());
  return readLegacyNumberingFormats(ydoc);
}

export function writeNumberingFormats(ydoc: YDoc, formats: NumberingFormatDefinition[]): void {
  const map = getNumberingFormatsMap(ydoc);
  ydoc.transact(() => {
    // Fold forward anything still sitting under the legacy key so it isn't lost once
    // this write starts the new map (readNumberingFormats stops checking the legacy
    // key as soon as the new map is non-empty).
    for (const format of readLegacyNumberingFormats(ydoc)) {
      if (!map.has(format.id)) map.set(format.id, format);
    }
    for (const format of formats) map.set(format.id, format);
  }, LOCAL_SETTINGS_ORIGIN);
}

export function observeNumberingFormats(ydoc: YDoc, handler: DocumentSettingsObserveHandler): () => void {
  const map = getNumberingFormatsMap(ydoc);
  const wrapped = (_event: unknown, transaction: { origin: unknown }): void => {
    handler({ isLocal: isLocalOrigin(transaction.origin) });
  };
  map.observe(wrapped);
  return () => {
    map.unobserve(wrapped);
  };
}

export function observeDocumentSettings(ydoc: YDoc, handler: DocumentSettingsObserveHandler): () => void {
  const map = getDocumentSettingsMap(ydoc);
  const wrapped = (_event: unknown, transaction: { origin: unknown }): void => {
    handler({ isLocal: isLocalOrigin(transaction.origin) });
  };
  map.observe(wrapped);
  return () => {
    map.unobserve(wrapped);
  };
}
